import React, { useState, useEffect } from 'react';
import './ProfileCompletion.css';
import {
  Box,
  LinearProgress,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Paper,
  IconButton,
  Collapse,
  Skeleton,
  Alert
} from '@mui/material';
import {
  CheckCircle,
  RadioButtonUnchecked,
  ExpandMore,
  ExpandLess,
  School,
  Work,
  EmojiEvents,
  Assignment,
  AccountBalance,
  VerifiedUser,
  Info
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { get } from '../../utility/fetch2';

const ProfileCompletionStatus = () => {
  const [completionData, setCompletionData] = useState({});
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const sections = [
    {
      key: 'education',
      label: 'Education',
      icon: <School />,
      sessionKey: 'isEducationCompleted'
    },
    {
      key: 'workExperience',
      label: 'Work Experience',
      icon: <Work />,
      sessionKey: 'isWorkExperienceCompleted'
    },
    {
      key: 'certifications',
      label: 'Certifications',
      icon: <Assignment />,
      sessionKey: 'isCertificateCompleted'
    },
    {
      key: 'licenses',
      label: 'Licenses',
      icon: <VerifiedUser />,
      sessionKey: 'isLicenceCompleted'
    },
    {
      key: 'associations',
      label: 'Associations',
      icon: <AccountBalance />,
      sessionKey: 'isAssociationCompleted'
    },
    {
      key: 'awards',
      label: 'Awards',
      icon: <EmojiEvents />,
      sessionKey: 'isAwardsCompleted'
    }
  ];

  useEffect(() => {
    checkCompletionStatus();
  }, []);

  const checkCompletionStatus = async () => {
    try {
      setLoading(true);
      
      // Get completion status from session storage first
      const sessionStatus = {};
      sections.forEach(section => {
        const status = sessionStorage.getItem(section.sessionKey);
        sessionStatus[section.key] = status === 'true';
      });

      // Also fetch current profile data to double-check
      const userId = sessionStorage.getItem('userId');
      if (userId) {
        try {
          const response = await get(`/employeeaccount/list/employee/${userId}`);
          if (response?.statusCode === 200 && response.data) {
            const profile = response.data;
            
            // Update completion status based on actual data
            const actualStatus = {
              education: profile.educations?.length > 0,
              workExperience: profile.workExperiences?.length > 0,
              certifications: profile.certificates?.length > 0,
              licenses: profile.licenses?.length > 0,
              associations: profile.associations?.length > 0,
              awards: profile.awardAndRecognitions?.length > 0
            };

            setCompletionData({ ...sessionStatus, ...actualStatus });
          } else {
            setCompletionData(sessionStatus);
          }
        } catch (apiError) {
          console.error('API error, using session storage:', apiError);
          setCompletionData(sessionStatus);
        }
      } else {
        setCompletionData(sessionStatus);
      }
    } catch (error) {
      console.error('Error checking completion status:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCompletionPercentage = () => {
    const completed = sections.filter(section => completionData[section.key]).length;
    return Math.round((completed / sections.length) * 100);
  };

  const getCompletedCount = () => {
    return sections.filter(section => completionData[section.key]).length;
  };

  const handleSectionClick = (sectionKey) => {
    // Navigate to career page and scroll to specific section
    navigate('/career', { state: { openSection: sectionKey } });
  };

  const handleToggleExpand = (e) => {
    e.stopPropagation();
    setExpanded(!expanded);
  };

  if (loading) {
    return (
      <Box sx={{ 
        m: 1, 
        p: 2, 
        backgroundColor: 'transparent',
        borderRadius: 1
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Skeleton variant="text" width={120} height={20} />
          <Skeleton variant="rectangular" width={40} height={24} sx={{ borderRadius: 1 }} />
        </Box>
        <Skeleton variant="rounded" width="100%" height={8} sx={{ mb: 1 }} />
        <Skeleton variant="text" width={80} height={16} />
      </Box>
    );
  }

  const completionPercentage = getCompletionPercentage();
  const completedCount = getCompletedCount();
  const isIncomplete = completionPercentage < 100;

  // Hide component if profile is complete
  if (completionPercentage === 100) {
    return null;
  }

  return (
    <Box sx={{ m: 1 }}>
      {/* Motivational Alert for Incomplete Profiles */}
      {isIncomplete && (
        <Alert 
          severity="info" 
          icon={<Info />}
          sx={{ 
            mb: 2,
            backgroundColor: 'rgba(33, 150, 243, 0.08)',
            border: '1px solid rgba(33, 150, 243, 0.2)',
            borderRadius: 2,
            '& .MuiAlert-icon': {
              color: 'primary.main'
            }
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 500 }}>
            To begin to receive bookings, you need to complete your profile.
          </Typography>
        </Alert>
      )}

      {/* Profile Completion Card */}
      <Box 
        sx={{ 
          backgroundColor: 'transparent', // Seamless with sidebar
          borderRadius: 2,
          border: '1px solid rgba(0, 0, 0, 0.06)',
          overflow: 'hidden',
          transition: 'all 0.2s ease',
          '&:hover': { 
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
            borderColor: 'rgba(0, 0, 0, 0.1)'
          }
        }}
      >
        <Box 
          sx={{ 
            p: 2, 
            cursor: 'pointer',
            transition: 'background-color 0.2s ease',
            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
          }}
          onClick={handleToggleExpand}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="subtitle2" fontWeight="600" color="text.primary">
              Profile Completion
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip 
                label={`${completedCount}/${sections.length}`}
                size="small"
                color={completionPercentage === 100 ? 'success' : 'primary'}
                variant={completionPercentage === 100 ? 'filled' : 'outlined'}
                sx={{
                  fontWeight: 600,
                  fontSize: '0.75rem'
                }}
              />
              <IconButton size="small" sx={{ p: 0.5 }}>
                {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              </IconButton>
            </Box>
          </Box>
          
          <LinearProgress 
            variant="determinate" 
            value={completionPercentage}
            sx={{ 
              height: 10, 
              borderRadius: 5,
              backgroundColor: 'rgba(0, 0, 0, 0.08)',
              mb: 1,
              '& .MuiLinearProgress-bar': {
                borderRadius: 5,
                backgroundColor: completionPercentage === 100 ? 'success.main' : 'primary.main',
                transition: 'all 0.3s ease'
              }
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
              {completionPercentage}% Complete
            </Typography>
            {completionPercentage === 100 && (
              <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                Profile Complete ✓
              </Typography>
            )}
          </Box>
        </Box>

        <Collapse in={expanded}>
          <List dense sx={{ pt: 0, backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
            {sections.map((section) => (
              <ListItem
                key={section.key}
                button
                onClick={() => handleSectionClick(section.key)}
                sx={{
                  py: 1,
                  px: 2,
                  transition: 'all 0.2s ease',
                  '&:hover': { 
                    backgroundColor: 'rgba(0, 0, 0, 0.06)',
                    transform: 'translateX(4px)'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {completionData[section.key] ? (
                    <CheckCircle color="success" fontSize="small" />
                  ) : (
                    <RadioButtonUnchecked color="action" fontSize="small" />
                  )}
                </ListItemIcon>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {React.cloneElement(section.icon, { 
                    fontSize: 'small',
                    color: completionData[section.key] ? 'success' : 'action'
                  })}
                </ListItemIcon>
                <ListItemText 
                  primary={section.label}
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: completionData[section.key] ? 600 : 400,
                    color: completionData[section.key] ? 'success.main' : 'text.secondary'
                  }}
                />
                
              </ListItem>
            ))}
          </List>
        </Collapse>
      </Box>
    </Box>
  );
};

export default ProfileCompletionStatus;